import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { CheckSquare, CalendarClock, FileWarning, Activity, Users, ChevronRight } from 'lucide-react';
import { TableSkeleton, ErrorState } from '../../components/ui';

const fmtD = (d) => { if (!d) return '—'; try { return format(parseISO(String(d).slice(0, 10)), 'd MMM'); } catch { return String(d); } };
const daysAgo = (d) => { if (!d) return null; try { return Math.round((Date.now() - parseISO(String(d).slice(0, 10)).getTime()) / 86400000); } catch { return null; } };
const nextKeyDate = (sc) => {
  const opts = [['Plan review', sc.plan_review_due], ['Progress report', sc.progress_report_due], [sc.next_release_info || 'Release', sc.next_release_date]]
    .filter(([, d]) => d).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  return opts[0] || null;
};

// "Today" — a coordinator's working view: oversight counts, today's tasks, upcoming key dates,
// engagements needing a note, recent activity — plus practice-wide caseload capacity vs targets.
export default function MyDay({ onOpen, oversight, onOversight }) {
  const [coord, setCoord] = useState('');
  const [day, setDay] = useState(null);
  const [caseload, setCaseload] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const monthStart = (() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-01`; })();

  // A failed load says so, with a retry. It used to leave the screen blank, which reads as "nothing due".
  const load = useCallback(() => {
    setLoading(true); setError(null);
    Promise.all([
      api.get('/support-coordination/my-day', { params: { coordinator_id: coord || undefined } }),
      api.get('/support-coordination/caseload', { params: { from: monthStart } }),
    ]).then(([d, c]) => { setDay(d.data); setCaseload(c.data || []); })
      .catch((e) => setError(e.response?.data?.message || 'We couldn’t load today’s work. Check your connection, then try again.'))
      .finally(() => setLoading(false));
  }, [coord, monthStart]);
  useEffect(() => { load(); }, [load]);

  const saveTarget = async (c, field, value) => {
    try {
      await api.put(`/support-coordination/coordinator-targets/${c.coordinator_id}`, {
        weekly_hours_target: field === 'weekly_hours_target' ? value : c.weekly_hours_target,
        caseload_target: field === 'caseload_target' ? value : c.caseload_target,
      });
      load();
    } catch { toast.error('Could not save target'); }
  };

  // One neutral icon tile per section: colour is kept for state (overdue in red), not decoration.
  const Section = ({ icon: Icon, title, count, children }) => (
    <div className="card p-0 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-100 text-slate-500"><Icon size={16} aria-hidden="true" /></span>
        <h2 className="font-bold text-slate-800 text-sm">{title}</h2>
        <span className="badge-gray !text-[10px]">{count}</span>
      </div>
      {children}
    </div>
  );
  const Empty = ({ text }) => <p className="px-5 py-6 text-sm text-slate-500 text-center">{text}</p>;
  const Row = ({ onClick, children }) => (
    <button type="button" onClick={onClick} className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0">
      {children}<ChevronRight size={15} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
    </button>
  );

  const ovChips = oversight ? [
    ['Missing notes', (oversight.missingNotes || []).length, 'bg-amber-50 text-amber-700 border-amber-100'],
    ['Overdue tasks', (oversight.overdueTasks || []).length, 'bg-red-50 text-red-700 border-red-100'],
    ['Reports due', (oversight.reportsDue || []).length, 'bg-amber-50 text-amber-700 border-amber-100'],
    ['Budgets at risk', (oversight.budgetsAtRisk || []).length, 'bg-red-50 text-red-700 border-red-100'],
    ['Open complaints', (oversight.openComplaints || []).length, 'bg-red-50 text-red-700 border-red-100'],
    ['Consents expiring', (oversight.expiringConsents || []).length, 'bg-amber-50 text-amber-700 border-amber-100'],
  ].filter(([, n]) => n > 0) : [];

  return (
    <div>
      {/* Oversight roll-up — merged into Today (SC redesign F7); detail lists live in Insights */}
      {ovChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {ovChips.map(([l, n, cls]) => (
            <button type="button" key={l} onClick={onOversight}
              className={clsx('inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors duration-150 hover:brightness-95 tabular-nums', cls)}>
              {l} · {n}
            </button>
          ))}
          <button type="button" onClick={onOversight} className="text-xs font-semibold text-primary-600 hover:text-primary-700 inline-flex items-center gap-0.5">Details <ChevronRight size={13} aria-hidden="true" /></button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500">Today across {coord ? 'this coordinator’s' : 'the whole'} caseload.</p>
        <select value={coord} onChange={(e) => setCoord(e.target.value)} aria-label="Coordinator" className="input-field !w-auto text-sm">
          <option value="">All coordinators</option>
          {caseload.map((c) => <option key={c.coordinator_id} value={c.coordinator_id}>{c.first_name} {c.last_name}</option>)}
        </select>
      </div>

      {loading ? <div className="card overflow-hidden"><TableSkeleton rows={6} cols={3} /></div>
        : error ? <div className="card"><ErrorState message={error} onRetry={load} /></div>
          : !day ? null : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section icon={CheckSquare} title="Tasks due and overdue" count={day.tasks.length}>
              {day.tasks.length === 0 ? <Empty text="Nothing due. You’re on top of it." /> : day.tasks.map((t) => {
                const overdue = t.due_date && daysAgo(t.due_date) > 0;
                return (
                  <Row key={t.id} onClick={() => onOpen(t.coordination_id)}>
                    <span className="min-w-0"><span className="text-sm text-slate-700 truncate block">{t.title}</span>
                      <span className={clsx('text-[11px]', overdue ? 'text-red-600 font-medium' : 'text-slate-500')}>{t.first_name} {t.last_name} · due {fmtD(t.due_date)}{overdue ? ' (overdue)' : ''}</span></span>
                  </Row>
                );
              })}
            </Section>

            <Section icon={CalendarClock} title="Key dates (next 14 days)" count={day.keyDates.length}>
              {day.keyDates.length === 0 ? <Empty text="No key dates coming up." /> : day.keyDates.map((k) => {
                const nk = nextKeyDate(k);
                return (
                  <Row key={k.id} onClick={() => onOpen(k.id)}>
                    <span className="min-w-0"><span className="text-sm text-slate-700 truncate block">{k.first_name} {k.last_name}</span>
                      <span className="text-[11px] text-slate-500">{nk ? `${nk[0]} · ${fmtD(nk[1])}` : ''}</span></span>
                  </Row>
                );
              })}
            </Section>

            <Section icon={FileWarning} title="Needs a note" count={day.needNote.length}>
              {day.needNote.length === 0 ? <Empty text="Every engagement has a recent note." /> : day.needNote.map((n) => (
                <Row key={n.id} onClick={() => onOpen(n.id)}>
                  <span className="min-w-0"><span className="text-sm text-slate-700 truncate block">{n.first_name} {n.last_name}</span>
                    <span className="text-[11px] text-slate-500">{n.last_billable ? `last note ${daysAgo(n.last_billable)}d ago` : 'no billable note yet'}</span></span>
                </Row>
              ))}
            </Section>

            <Section icon={Activity} title="Recent activity" count={day.recent.length}>
              {day.recent.length === 0 ? <Empty text="No recent notes." /> : day.recent.map((r) => (
                <Row key={r.id} onClick={() => onOpen(r.coordination_id)}>
                  <span className="min-w-0"><span className="text-sm text-slate-700 truncate block">{r.summary || 'Case note'}</span>
                    <span className="text-[11px] text-slate-500">{r.first_name} {r.last_name} · {fmtD(r.note_date)}</span></span>
                </Row>
              ))}
            </Section>
          </div>

          {/* Caseload capacity */}
          <div className="card p-0 overflow-hidden mt-4">
            <div className="px-5 py-3.5 border-b border-slate-100 flex flex-wrap items-center gap-2.5">
              <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center"><Users size={16} aria-hidden="true" /></span>
              <h2 className="font-bold text-slate-800 text-sm">Caseload capacity</h2>
              <span className="text-[11px] text-slate-500 ml-1">hours this month · editable targets</span>
            </div>
            {caseload.length === 0 ? <Empty text="No coordinators with a caseload yet." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white text-xs text-slate-600 border-b border-slate-200">
                    <tr>
                      <th scope="col" className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">Coordinator</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Active</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Caseload target</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Hours (mo)</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Weekly hrs target</th>
                      <th scope="col" className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">Load</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {caseload.map((c) => {
                      const hours = (Number(c.billable_minutes) || 0) / 60;
                      const loadPct = c.caseload_target ? Math.min(150, (c.active_engagements / c.caseload_target) * 100) : null;
                      return (
                        <tr key={c.coordinator_id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-700">{c.first_name} {c.last_name}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">{c.active_engagements}</td>
                          <td className="px-4 py-3 text-right">
                            <input type="number" defaultValue={c.caseload_target ?? ''} placeholder="—" aria-label={`Caseload target for ${c.first_name} ${c.last_name}`} className="input-field !py-1 !w-20 text-sm text-right"
                              onBlur={(e) => { const v = e.target.value; if (v !== String(c.caseload_target ?? '')) saveTarget(c, 'caseload_target', v); }} />
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">{hours.toFixed(1)}</td>
                          <td className="px-4 py-3 text-right">
                            <input type="number" step="0.5" defaultValue={c.weekly_hours_target ?? ''} placeholder="—" aria-label={`Weekly hours target for ${c.first_name} ${c.last_name}`} className="input-field !py-1 !w-20 text-sm text-right"
                              onBlur={(e) => { const v = e.target.value; if (v !== String(c.weekly_hours_target ?? '')) saveTarget(c, 'weekly_hours_target', v); }} />
                          </td>
                          <td className="px-4 py-3 w-40">
                            {loadPct == null ? <span className="text-xs text-slate-500">Set a target</span> : (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                                  {/* Colour only once a caseload is near or over its target (same thresholds). */}
                                  <div className={clsx('h-full rounded-full', loadPct > 100 ? 'bg-red-500' : loadPct > 85 ? 'bg-amber-500' : 'bg-slate-400')} style={{ width: `${Math.min(100, loadPct)}%` }} />
                                </div>
                                <span className="text-[11px] text-slate-500 tabular-nums w-9 text-right">{Math.round(loadPct)}%</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
